import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Resource } from '../models/resource.model';
import { matchVal, matchOptionsVal, SingleFilter } from '../models/filter-data/single-filter.model';
import { SemanticGroupTextualForms, TextualForm } from 'src/app/ricerca/interfaces/semantic-group-textual-forms.model';
import { Entry } from 'src/app/ricerca/interfaces/entry.model';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  /**
   * Costruttore del servizio UtilityService
   * @param translate {TranslateService} servizio che gestisce le lingue disponibili per l'applicazione
   * @param _snackBar {MatSnackBar} componente di material angular per la visualizzazione di brevi messaggi a fondo pagina
   */
  constructor(
    private translate: TranslateService,
    private _snackBar: MatSnackBar
  ) { }

  /**
   * Metodo che recupera le stringhe dell'interfaccia nella lingua in uso nel browser
   * @param key {string} chiave di ricerca della stringa
   * @param obj {Object} coppie chiave-valore per operare eventuali sostituzioni di variabili nelle stringhe 
   * @returns string
   */
  getTranslatedString(key: string, obj?: Object): string {
    let temp: string;
    key = key.replace(/\s/g, '');
    if (obj) {
      this.translate.get(key, obj).pipe(take(1)).subscribe(data => {
        temp = data;
      });
    } else {
      this.translate.get(key).pipe(take(1)).subscribe(data => {
        temp = data;
      });
    }
    return temp;
  }

  /**
   * Metodo per la visualizzazione di una snackbar di notifica
   * @param messaggio {string} messaggio che deve essere visualizzato nella snackbar
   */
  gestioneSnackbar(messaggio: string) {
    this._snackBar.open(messaggio, '', {
      duration: 2000,
      panelClass: 'alert-success'
    });
  }

  /**
   * Metodo per la visualizzazione di una snackbar di notifica errore
   * @param messaggio {string} messaggio che deve essere visualizzato nella snackbar
   */
  gestioneSnackbarErrore(messaggio: string) {
    this._snackBar.open(messaggio, '', {
      duration: 4000,
      panelClass: 'alert-danger'
    });
  }

  gestioneSnackbarErrorePulsante(messaggio: string) {
    this._snackBar.open(messaggio, 'Ho capito');
  }

  /**
   * Metodo per il recupero del titolo del trattato di cui fa parte una risorsa
   * @param resource {Resource} risorsa corrente
   * @param tractatesList {Resource[]} elenco dei trattati disponibili
   * @returns il titolo del trattato capostipite o stringa vuota se la risorsa è un trattato
   */
  getTractateTitle(resource: Resource, tractatesList: Resource[]): string {
    if (resource.type === 'Tractate') {
      return '';
    } else {
      let indiceTrattato = resource.index.split('.')[0];
      let trattatoPadre = tractatesList.find(risorsa => {
        return risorsa.index === indiceTrattato;
      });
      return trattatoPadre.title + ' - ';
    }
  }

  /**
   * Metodo per la costruzione dinamica di filtri singoli
   * @param fieldName {string} nome del campo
   * @param filterValue {any} valore da ricercare per il campo
   * @param matchCase {boolean} se è case sensitive
   * @param wholeWord {boolean} se è cercata la parola esatta
   */
  createFilter(fieldName: string, filterValue: any, matchCase: boolean, wholeWord: boolean): SingleFilter { 
    let matchValue: matchVal = matchVal.like;
    let matchOptionValue: matchOptionsVal[] = [];
    if(fieldName === 'type') {
      matchValue = matchVal.equals;
    } else if (typeof filterValue === 'string') {
      let startsWith = filterValue.startsWith('*') || filterValue.startsWith('?');
      let endsWith = filterValue.endsWith('*') || filterValue.endsWith('?');
      if (startsWith || endsWith || matchCase || wholeWord) {
        // if (endsWith) { //Non attualmente in uso per la parte di glossari
        //   matchOptionValue.push(matchOptionsVal.startsWith);
        // }
        // if (startsWith) {
        //   matchOptionValue.push(matchOptionsVal.endsWith);
        // }
        if (matchCase) {
          matchOptionValue.push(matchOptionsVal.matchcase);
        }
        if (wholeWord) {
          matchOptionValue.push(matchOptionsVal.wholeWord);
        }
      }
    } else if(typeof filterValue === 'boolean' || typeof filterValue === 'number') {
      matchValue = matchVal.equals;
    }
    return <SingleFilter>{ field: fieldName, value: filterValue, match: matchValue, matchOptions: matchOptionValue }
  }

  /**
   * Metodo che consente di ordinare una lista di elementi sulla base del loro indice costruito come numerazione puntata
   * @param elenco {any[]} lista di elementi 
   */
  sortArrayByIndex(elenco: any[]): any[] {
    return elenco.sort((a, b) => {
      let aSplitIndex = a.index.split('.');
      let bSplitIndex = b.index.split('.');
      for (let i = 0; i < aSplitIndex.length; i++) {
        if (+aSplitIndex[i] > +bSplitIndex[i]) {
          return 1;
        }
        if (+aSplitIndex[i] < +bSplitIndex[i]) {
          return -1;
        }
      }
    });
  }

  mapTextualForm(tf: TextualForm): TextualForm {
    return <TextualForm>{
      formType: tf?.formType ? tf.formType : (('type' in tf) ? tf['type'] : ''),
      label: tf.label,
      morphology: [...tf.morphology],
      // form: tf.form,
      formInstanceName: tf.formInstanceName,
      // lexicalEntry: tf.lexicalEntry,
      lexicalEntryInstanceName: tf.lexicalEntryInstanceName,
      // targetSense: tf.targetSense,
      targetSenseInstanceName: tf?.targetSenseInstanceName ? tf.targetSenseInstanceName : '',
      definition: tf.definition
    };
  }

  mapTextualFormArray(tfArray: TextualForm[]): TextualForm[] {
    let result: TextualForm[] = [];
    tfArray.forEach(tf => result.push(this.mapTextualForm(tf)));
    return result;
  }

  splitSemanticGroupRelations(semanticRelation: SemanticGroupTextualForms): SemanticGroupTextualForms[] {
    let groupedTarget = this.groupArrayOfObjects(semanticRelation.target, 'targetSenseInstanceName');
    let results: SemanticGroupTextualForms[] = [];
    for (let group in groupedTarget) {
      results.push(<SemanticGroupTextualForms>{
        semanticRelation: semanticRelation.semanticRelation,
        distance: semanticRelation.distance,
        senseInstanceName: semanticRelation.senseInstanceName,
        targetSenseInstanceName: group,
        target: groupedTarget[group],
        targetLabel: groupedTarget[group].filter((e: TextualForm) => e.formType === 'canonicalForm')[0].label,
        targetSenseDescription: groupedTarget[group].filter((e: TextualForm) => e.formType === 'canonicalForm')[0].definition,
        targetLemma: groupedTarget[group][0].lexicalEntryInstanceName
      });
    }
    console.log('test inserimento descrizione');
    console.log(results);
    return results;
  }

  groupArrayOfObjects(list: any[], key: string): any[] {
    return list.reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  mapEntry(entry: Entry): Entry {
    return <Entry> {
      // sense: entry.sense,
      senseInstanceName: entry?.senseInstanceName? entry.senseInstanceName : '',
      // lexicalEntry: entry.lexicalEntry,
      lexicalEntryInstanceName: entry.lexicalEntryInstanceName,
      pos: entry.pos,
      lemma: entry.lemma,
      // hasChildren: entry.hasChildren,
      // label: entry.label,
      definition: entry.definition,
      // concept: entry.concept,
      conceptInstanceName: entry.conceptInstanceName
      }
  }

  mapEntryArray(entryArray: Entry[]): Entry[] {
    let result: Entry[] = [];
    entryArray.forEach(e => result.push(this.mapEntry(e)));
    return result;
  }
}
